import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogContent,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Calendar,
  Clock,
  MapPin,
  Plus,
  Eye
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import BookingForm from './BookingForm';
import api from '../../utils/api';

const SupplierProfileBooking = ({ businessId, businessName }) => {
  const { user } = useAuth();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedServiceType, setSelectedServiceType] = useState(null);

  useEffect(() => {
    if (businessId) {
      fetchServiceTypes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  const fetchServiceTypes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/service-types/business/${businessId}/public`);
      setServiceTypes(response.data.serviceTypes);
    } catch (error) {
      setError('Failed to load available services');
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = (serviceType) => {
    if (!user) {
      // Redirect to login or show login modal
      setError('Please log in to book a service');
      return;
    }
    
    setSelectedServiceType(serviceType);
    setShowBookingForm(true);
  };

  const handleBookingSuccess = (_booking) => {
    setShowBookingForm(false);
    setSelectedServiceType(null);
    // Show success message or redirect
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (serviceTypes.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No services are currently available for booking.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Book a Service with {businessName}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Eye />}
              onClick={() => setShowBookingForm(true)}
            >
              View All Services
            </Button>
          </Box>

          <Grid container spacing={2}>
            {serviceTypes.slice(0, 3).map((serviceType) => (
              <Grid item xs={12} sm={6} md={4} key={serviceType._id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {serviceType.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {serviceType.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Clock size={16} />
                      <Typography variant="body2">
                        {serviceType.duration} minutes
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <MapPin size={16} />
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {serviceType.locationType.replace('_', ' ')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" color="primary">
                        ${serviceType.pricing.basePrice}
                      </Typography>
                      {serviceType.isBlockerService && (
                        <Chip label="Blocker Service" size="small" color="warning" />
                      )}
                    </Box>
                  </CardContent>
                  
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<Calendar />}
                      onClick={() => handleBookService(serviceType)}
                    >
                      Book Now
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {serviceTypes.length > 3 && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Plus />}
                onClick={() => setShowBookingForm(true)}
              >
                View All {serviceTypes.length} Services
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Booking Form Dialog */}
      <Dialog
        open={showBookingForm}
        onClose={() => setShowBookingForm(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <BookingForm
            businessId={businessId}
            serviceTypeId={selectedServiceType?._id}
            onBookingSuccess={handleBookingSuccess}
            onClose={() => setShowBookingForm(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupplierProfileBooking;
